import { type MutationFunctionOptions } from '@apollo/client';
import { createContext, useContext } from 'react';

import {
  type AtlasGqlHorizonDamage,
  type AtlasGqlPictureUpdateInput,
  type AtlasGqlUpdatePicturesMutation,
  type Exact,
} from 'types/atlas-graphql';

import { type CardState, type PaneSide } from './types';

type ImageViewerContextType = {
  isPictureReview: boolean;
  leftPaneDamage?: Partial<AtlasGqlHorizonDamage>;
  rightPaneDamage?: Partial<AtlasGqlHorizonDamage>;
  setDamage: (paneSide: PaneSide, newDamage: AtlasGqlHorizonDamage) => void;
  setPicture: (
    paneSide: PaneSide,
    newPictureId?: string | null,
    newObservationId?: string | null,
    newFilter?: string | string[] | null
  ) => void;
  setObservation: (paneSide: PaneSide, newObservationId: string | null) => void;
  setFilter: (paneSide: PaneSide, newFilter: string) => void;
  setLevels: (paneSide: PaneSide, newLevels: [number, number]) => void;
  setCardState: (paneSide: PaneSide, updatedCardState: Partial<CardState>) => void;
  updatePictures: (
    options?:
      | MutationFunctionOptions<
          AtlasGqlUpdatePicturesMutation,
          Exact<{
            picturesToUpdate: AtlasGqlPictureUpdateInput | AtlasGqlPictureUpdateInput[];
          }>
        >
      | undefined
  ) => void;
  updatePicturesLoading: boolean;
  navigationDisabled: boolean;
  setNavigationDisabled: (disabled: boolean) => void;
  isMerging: boolean;
  setIsMerging: (bool: boolean) => void;
  shouldRefetchLeftObservation: boolean;
  shouldRefetchRightObservation: boolean;
  setShouldRefetchObservation: (paneSide: PaneSide, shouldRefetch: boolean) => void;
  shouldRefetchLeftImage: boolean;
  shouldRefetchRightImage: boolean;
  setShouldRefetchImage: (paneSide: PaneSide, shouldRefetch: boolean) => void;
  setModalInspectionsAndPictures: (inspectionPictureObject: any) => any;
};

const defaultImageViewerContext = {
  isPictureReview: false,
  leftPaneDamage: undefined,
  rightPaneDamage: undefined,
  setDamage: () => {},
  setPicture: () => {},
  setObservation: () => {},
  setFilter: () => {},
  setLevels: () => {},
  setCardState: () => {},
  updatePictures: async () => {},
  updatePicturesLoading: false,
  navigationDisabled: false,
  setNavigationDisabled: () => {},
  isMerging: false,
  setIsMerging: () => {},
  shouldRefetchLeftObservation: false,
  shouldRefetchRightObservation: false,
  setShouldRefetchObservation: () => {},
  shouldRefetchLeftImage: false,
  shouldRefetchRightImage: false,
  setShouldRefetchImage: () => {},
  setModalInspectionsAndPictures: () => [],
};

export const ImageViewerContext = createContext<ImageViewerContextType>(defaultImageViewerContext);

export const useImageViewerContext = () => useContext(ImageViewerContext);
